import { forwardRef } from "react";
import { SheetFrame, F } from "@/components/sheets/SheetFrame";
import { Table } from "@/components/sheets/SheetTable";
import { RH } from "@/lib/bomPages";

const X = 14, Y = 36, LBL = 30, BW = 214, BX = X + LBL;

const Bar = ({ l, s }) => {
  const k = BW / s.bar, y = Y + l.y;
  let cx = BX + s.trim * k;
  return (
    <g data-testid="cut-bar">
      <text x={X} y={y + 3.4} fontSize={2.1} fontFamily={F} fill="#000">{`P${l.i + 1}  × ${l.p.n} bar${l.p.n > 1 ? "s" : ""}`}</text>
      <rect x={BX} y={y} width={BW} height={4.6} fill="#E5E7EB" stroke="#000" strokeWidth={0.2} />
      {l.p.cuts.map((c, j) => {
        const w = c.len * k, x = cx;
        cx += (c.len + s.kerf) * k;
        const t = w > 22 ? `${c.len}  ${c.mark}·${c.tag}` : w > 8 ? `${c.len}` : "";
        return (
          <g key={j}>
            <rect x={x} y={y} width={w} height={4.6} fill={j % 2 ? "#BAE6FD" : "#E0F2FE"} stroke="#0E7490" strokeWidth={0.2} />
            {t && <text x={x + w / 2} y={y + 3.2} fontSize={1.8} fontFamily={F} fill="#000" textAnchor="middle">{t}</text>}
          </g>
        );
      })}
      <text x={BX + BW + 3} y={y + 3.4} fontSize={2.1} fontFamily={F} fill="#000">{`cut ${l.p.used} · offcut ${l.p.offcut}`}</text>
    </g>
  );
};

const CutLine = ({ l, s }) => {
  const y = Y + l.y, g = l.g;
  if (l.t === "head") {
    return (
      <g>
        <rect x={X} y={y} width={284} height={5.6} fill="#0E7490" />
        <text x={X + 1.5} y={y + 4} fontSize={2.5} fontFamily={F} fill="#fff" fontWeight="bold">
          {`${g.tag ? `${g.tag} · ` : ""}${g.name}${g.code ? ` (${g.code})` : ""} · ${g.system} · ${g.pieces} pcs · ${g.bars} bar${g.bars !== 1 ? "s" : ""} · waste ${g.waste}%`.slice(0, 150)}
        </text>
      </g>
    );
  }
  if (l.t === "over") return <text x={X} y={y + 3.4} fontSize={2.1} fontFamily={F} fill="#B91C1C">{`Longer than usable bar: ${l.c.len} mm ${l.c.mark}·${l.c.tag} — order cut-to-size or joint`}</text>;
  return <Bar l={l} s={s} />;
};

const projectRows = (tb, p) => {
  const s = p.settings;
  return [["Project", tb.project_name], ["Client", tb.client], ["Architect / Consultant", tb.architect], ["Fabricator / Contractor", tb.contractor || tb.company],
    ["Items / units", `${p.nItems} items · ${p.qty} units`], ["Total opening area", `${p.area.toFixed(2)} m²`],
    ["Bar stock", `${s.bar} mm · saw cut ${s.kerf} mm · trim ${s.trim} mm`], ["Optimisation", s.mode === "project" ? "Whole project (shared bars)" : "Item by item"],
    ["GST", `${s.gst}% (rates to be entered)`], ["Finish", tb.finish], ["Glazing", tb.glass_spec], ["Hardware", "IWIN-S accessories per item"], ["Prepared by", tb.drawn_by]];
};

export const BomSheet = forwardRef(({ tb, sheetNo, total, page }, ref) => {
  const sub = page.type === "cuts"
    ? `BAR ${page.settings.bar} mm · SAW CUT ${page.settings.kerf} mm · END TRIM ${page.settings.trim} mm · ${page.plan.bars} BARS · WASTE ${page.plan.waste}%`
    : page.suffix === "H" ? "COMBINED ACROSS ALL ITEMS · GROUPED BY IWIN-S PRODUCT · QUANTITIES INCLUDE ITEM QTY" : "QUANTITIES ONLY · RATES, AMOUNTS AND GST TO BE ENTERED";
  return (
    <SheetFrame svgRef={ref} item={{ tag: "" }} tb={tb} sheetNo={sheetNo} total={total} suffix={page.suffix} kind={page.kind} title={page.kind} subtitle={sub}
      scaleText="NTS" rows={projectRows(tb, page)}>
      <g data-testid={`bom-sheet-${page.suffix}`}>
        {page.type === "tables" && page.sections.map((sec, i) => (
          <Table key={i} x={X} y={34 + sec.y + 6} rh={RH} cols={sec.cols} rows={sec.rows} title={`${sec.title}${sec.cont ? " (CONT.)" : ""}`} />
        ))}
        {page.type === "cuts" && page.lines.map((l, i) => <CutLine key={i} l={l} s={page.settings} />)}
      </g>
      <text x={16} y={284.5} fontSize={2} fontFamily={F} fill="#555">
        {page.type === "cuts" ? "Best-fit decreasing bar optimisation · P = cutting pattern (× number of identical bars) · mitre cuts measured on the long point · verify before cutting." : "Generated from the elevation, assembly and IWIN-S accessory data. Verify quantities before ordering."}
      </text>
    </SheetFrame>
  );
});
BomSheet.displayName = "BomSheet";
